import { Check, Delete } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import { GridSortItem, GridColDef, GRID_TREE_DATA_GROUPING_FIELD } from '@mui/x-data-grid-pro';
import DeleteWorkOrderButton from './delete-work-order-button';
import { ReactElement } from 'react';
import { formatDate } from 'util/format-date';
import RowDetail from './row-detail';
import { lightTheme as theme } from 'theme';
import { lighten } from '@mui/material/styles';
// import EditWorkOrderButton from './edit-work-order';

// define default sort behavior here, could be an empty array
export const defaultSortModel: GridSortItem[] = [{ field: GRID_TREE_DATA_GROUPING_FIELD, sort: 'asc' }];

export const defaultBoxSx = {
	width: '100%',
	display: 'grid',
	height: 'calc(100vh - 120px)',
};

export const workOrderSx = {
	minWidth: 0,
	height: '100%',
	'& .work-order--error': {
		backgroundColor: lighten(theme.palette.error.main, 0.6),
		'&:hover': {
			backgroundColor: lighten(theme.palette.error.main, 0.5),
		},
		'&.Mui-selected': {
			backgroundColor: lighten(theme.palette.error.main, 0.4),
			'&:hover': {
				backgroundColor: lighten(theme.palette.error.main, 0.3),
			},
		},
	},
	'& .work-order--completed': {
		opacity: 0.6,
	},
};

export const getTreeDataPath = (row) => [
	row.devicePrefix,
	row.group,
	row.deviceName,
	row?.deletedAt ? row.protocolStep + '_' + row?.deletedAt : row.protocolStep,
];

export const groupingColDef = {
	headerName: 'Device prefix > group > device > protocol step',
};

export const getRowClass = (params) => {
	const isGroupingRow = String(params.id).startsWith('auto-generated-row');
	if (isGroupingRow) {
		return '';
	}
	const isTest = params.row.testId !== null;
	if (isTest) {
		return params.row.validationStatus === 'success'
			? 'work-order--completed'
			: params.row.isValidating
			? ''
			: 'work-order--error';
	} else {
		return params.row.completedAt !== null ? 'work-order--completed' : 'work-order--error';
	}
};

export const getDeliverables = (params) => (params.row.testId !== null ? <RowDetail {...params} /> : null);

export const baseColumns: GridColDef[] = [
	{
		field: 'id',
		headerName: 'ID',
	},
	{ field: 'devicePrefix', headerName: 'Device prefix' },
	{ field: 'group', headerName: 'Group' },
	{ field: 'deviceName', headerName: 'Device name', width: 120 },
	{
		field: 'protocolBlockDescription',
		headerName: 'Protocol block description',
		width: 120,
	},
	{ field: 'protocolStep', headerName: 'Protocol step', width: 80 },
	{ field: 'experimentId', headerName: 'Experiment ID', flex: 1 },
	{ field: 'temperature', headerName: 'Temperature (ºC)', width: 120 },
	{ field: 'testPhrase', headerName: 'Test phrase', width: 130 },
	{
		field: 'status',
		headerName: 'Status',
		description:
			'Success: File(s) have been uploaded and validated. Validating: Uploaded files are currently under validation. Failure: File(s) were uploaded but failed validation. Not started: No file(s) have been uploaded yet.',
		type: 'singleSelect',
		width: 100,
		valueOptions: ['success', 'failure', 'validating', 'not started'],
		valueGetter: (params) => {
			const isTest = params.row.testId !== null;
			if (isTest) {
				return params.row.validationStatus !== null
					? params.row.validationStatus
					: params.row.isValidating
					? 'validating'
					: 'not started';
			} else {
				return params.row.completedAt !== null ? 'success' : 'not started';
			}
		},
		renderCell: (params) => {
			if (params.rowNode?.type === 'group') {
				return <></>;
			}
			if (params.value === 'success') {
				return (
					<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
						<Check color="success" />
						{params.row?.completedBy && (
							<Typography variant="caption" sx={{ fontSize: '12px' }}>
								by {params.row.completedBy}
							</Typography>
						)}
						<Typography variant="caption" sx={{ fontSize: '12px' }}>
							at {formatDate(params.row.completedAt, '%Y-%m-%d')}
						</Typography>
					</Box>
				);
			}

			return (
				<Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
					{params.value}
				</Typography>
			);
		},
	},
	{
		field: 'elementIdentifier',
		headerName: 'Identifier',
		flex: 1,
		renderCell: (params) => {
			if (params.rowNode?.type === 'group') {
				return <></>;
			}

			return <Box sx={{ display: 'flex', flexDirection: 'column' }}>{params.value}</Box>;
		},
	},
	{
		field: 'duration',
		headerName: 'Duration (days)',
		width: 80,
		valueGetter: (params) => params.row?.document?.duration,
	},
	{ field: 'testId', headerName: 'Test ID' },
	{ field: 'createdAt', headerName: 'Created at' },
	{
		field: 'createdBy',
		headerName: 'Created by',
	},
];

export const columns: GridColDef[] = [
	...baseColumns,
	{
		field: 'deleted',
		headerName: 'Deleted',
		type: 'boolean',
		width: 120,
		valueGetter: (params) => params.row.deletedAt !== null,
	},
	{
		field: 'actions',
		type: 'actions',
		headerName: 'Actions',
		width: 80,
		getActions: (params) => {
			if (String(params.id).includes('auto-generated-row-null')) {
				return [];
			}
			const disabled = params.row.deletedAt !== null;
			const completed = params.row.completedAt !== null;
			const actions = [
				// <EditWorkOrderButton
				// 	id={params.id}
				// 	disabled={disabled || completed}
				// 	workOrder={params.row}
				// 	label="Edit"
				// 	icon={<Edit />}
				// 	showInMenu
				// 	closeMenuOnClick={false}
				// />,
				<DeleteWorkOrderButton
					id={params.id}
					disabled={disabled || completed}
					label="Delete"
					icon={<Delete />}
					showInMenu
					closeMenuOnClick={false}
				/>,
			] as ReactElement[];

			return actions;
		},
	},
];
